import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { PanelAction } from '@datachain/ui-sdk/components/app-panel';
import { AvatarEntity } from '@datachain/ui-sdk/components/avatar';
import { IBreadCrumbEntry } from '@datachain/ui-sdk/components/breadcrumb';
import { AppIcons } from '@datachain/ui-sdk/components/dc-icon';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { Store } from '@ngrx/store';
import { DxDataGridComponent } from 'devextreme-angular';
import {
	CellHoverChangedEvent,
	SelectionChangedEvent,
} from 'devextreme/ui/data_grid';
import { OrderedSet } from 'immutable';
import {
	BehaviorSubject,
	concat,
	debounceTime,
	filter,
	firstValueFrom,
	map,
	Observable,
	of,
	ReplaySubject,
	switchMap,
	takeUntil,
	tap,
	timer,
} from 'rxjs';
import {
	checkStringEquality,
	DcDxGrids,
	DEFAULT_PAGE_SIZE,
	DEFAULT_PAGE_SIZES,
	DxFilterElem,
	DxHeaderFilterItem,
	DxOperations,
	HeaderFilterDefaultMapper,
	NoopValue,
} from 'src/app/core/dx-types';

import { DxListsStorage, IDxList } from '../../../core/dx-lists.storage';
import { goToGdHome, goToProjects } from '../../../core/store';
import { ActionIcons } from '../../../ui/app-actions.icons';
import { ComponentIcons } from '../../../ui/app-components.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { GdIcons } from '../../../ui/app-gd.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import { AppPublicProjectView } from '../../../ui/components/app-public-project-view/app-public-project-view.entity';
import { ActionIconsInjectable } from '../../../ui/ui.module';
import {
	ConnectorSelector,
	fetchPublicProjectDetails,
	initConnectorList,
	navigateToConnector,
	navigateToConnectorCreate,
	navigateToConnectorDuplicate,
	navigateToDepotFromConnector,
	openDeleteConfirmationPopup,
	refreshConnectorList,
	toggleConnectorStatus,
	updateIncludePublic,
} from '../../store';
import { ConnectorItemEntity } from './connector-item.entity';
import { ConnectorListEntity } from './connector-list.entity';

type RowAction = {
	description: string;
	actionName: string;
	icon: string;
	action: (...args: Array<unknown>) => void;
};

type ConnectorOverlayDetails = {
	detailsConnectorId: number | string | undefined;
};

@Component({
	selector: 'app-connector-list',
	templateUrl: './connector-list.component.html',
	styleUrls: ['./connector-list.component.scss'],
	inputs: ['projectLabel'],
})
export class ConnectorListComponent extends DcBaseComponent implements OnInit {
	protected readonly GdIcons = GdIcons;
	protected readonly DcIcons = DcIcons;
	protected readonly DcLoaders = DcLoaders;
	protected readonly ComponentIcons = ComponentIcons;
	protected readonly pageSize = DEFAULT_PAGE_SIZE;
	protected readonly allowPageSizes = DEFAULT_PAGE_SIZES;

	public paths: Array<IBreadCrumbEntry> = [];
	public ActionIcons = ActionIcons;
	public DxOperations = DxOperations;
	public panelActions: Array<PanelAction> = [];
	public currentlyHovered = -1;
	public toggleActions = false;
	public selectionCount = 0;
	public readonly disabledRowActions: Array<RowAction> = [
		{
			actionName: 'duplicate',
			description: $localize`:i18n=@@connector.list.action.duplicate:`,
			action: (item: unknown): void => {
				const connector = item as ConnectorItemEntity;
				this.duplicateConnector(connector);
			},
			icon: ActionIcons.DuplicateElement,
		},
		{
			actionName: 'activate',
			description: $localize`:i18n=@@general.action.activate:`,
			action: (item: unknown): void => {
				const connector = item as ConnectorItemEntity;
				this.toggleActivationStatus(connector);
			},
			icon: ActionIcons.ActivateElement,
		},
	];
	public readonly activeRowActions: Array<RowAction> = [
		{
			actionName: 'duplicate',
			description: $localize`:i18n=@@connector.list.action.duplicate:`,
			action: (item: unknown): void => {
				const connector = item as ConnectorItemEntity;
				this.duplicateConnector(connector);
			},
			icon: ActionIcons.DuplicateElement,
		},
		{
			actionName: 'deactivate',
			description: $localize`:i18n=@@general.action.deactivate:`,
			action: (item: unknown): void => {
				const connector = item as ConnectorItemEntity;
				this.toggleActivationStatus(connector);
			},
			icon: ActionIcons.DeactivateElement,
		},
	];

	public tagsHeaderFilter: Array<DxHeaderFilterItem> = [];
	public modifiedByHeaderFilter: Array<DxHeaderFilterItem> = [];
	public actionByHeaderFilter: Array<DxHeaderFilterItem> = [];

	public isImportedHeaderFilter = [
		{
			text: $localize`:i18n=@@connector.list.column.imported:`,
			value: ['isImported', '=', true],
		},
		{
			text: $localize`:i18n=@@connector.list.column.unimported:`,
			value: ['isImported', '=', false],
		},
	];

	public actifHeaderFilter = [
		{
			text: $localize`:i18n=@@general.column.actif:`,
			value: ['isActive', '=', true],
		},
		{
			text: $localize`:i18n=@@general.column.unactif:`,
			value: ['isActive', '=', false],
		},
	];

	public vo$: Observable<{
		isFirstLoad: boolean;
		isLoading: boolean;
		list: ConnectorListEntity;
		includePublic: boolean;
		openedOverlays: ConnectorOverlayDetails;
		isPublicProjectDetailsLoading: boolean;
		publicProjectDetails: AppPublicProjectView | null;
	}>;

	public loadState = (): IDxList | undefined =>
		this.dxStorage.getListState(this.dxLocalStorageKey);

	public saveState = (state: IDxList): void => {
		this.dxStorage.setListState(this.dxLocalStorageKey, {
			...state,
			searchText: '',
		});
	};

	@ViewChild(DxDataGridComponent)
	private readonly grid: DxDataGridComponent | null = null;

	private readonly connectorListSubject =
		new ReplaySubject<ConnectorListEntity>(1);

	private readonly openedOverlaySubject =
		new BehaviorSubject<ConnectorOverlayDetails>({
			detailsConnectorId: undefined,
		});

	private readonly firstLoadSubject = new BehaviorSubject<boolean>(true);

	public constructor(
		private readonly store: Store,
		private readonly connectorSelector: ConnectorSelector,
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons,
		private readonly dxStorage: DxListsStorage
	) {
		super();
		this.cmpId = 'connector-list';
		this.dxLocalStorageKey = DcDxGrids.ConnectorListStandalone;

		const targetConnector = this.openedOverlaySubject.pipe(
			filter((val) => val.detailsConnectorId !== undefined),
			switchMap((val) =>
				this.connectorSelector.getConnector$(val.detailsConnectorId as number)
			),
			map((e) => this.buildPublicProjectDetails(e))
		);

		this.vo$ = combineAll({
			isFirstLoad: this.firstLoadSubject,
			isLoading: this.connectorSelector.getLoading$(),
			list: this.connectorListSubject,
			includePublic: this.connectorSelector.includePublic$(),
			openedOverlays: this.openedOverlaySubject,
			isPublicProjectDetailsLoading:
				this.connectorSelector.isPublicProjectDetailsLoading$(),
			publicProjectDetails: concat(of(null), targetConnector),
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.store.dispatch(initConnectorList());

		combineAll({
			projectLabel: this.toObservable<string>('projectLabel'),
		})
			.pipe(
				takeUntil(this.onDestroy$),
				tap(({ projectLabel }) => {
					this.paths = [
						{
							label: $localize`:i18n=@@general.projects:`,
							isActive: true,
							callback: (): void => this.store.dispatch(goToProjects()),
						},
						{
							label: projectLabel,
							isActive: true,
							callback: (): void => this.store.dispatch(goToGdHome()),
						},
						{
							label: $localize`:i18n=@@connector.list.title:`,
							isActive: false,
							callback: (): void => {},
						},
					];
				})
			)
			.subscribe();

		of(null)
			.pipe(
				takeUntil(this.onDestroy$),
				tap(() => this.bootstrapIcons())
			)
			.subscribe();

		this.connectorSelector
			.getConnectorList$()
			.pipe(
				takeUntil(this.onDestroy$),
				debounceTime(320),
				tap((l) => {
					this.tagsHeaderFilter = l
						.extractUniqTags(NoopValue)
						.map(HeaderFilterDefaultMapper)
						.toArray();
					this.modifiedByHeaderFilter = l
						.extractModifiers(NoopValue)
						.map(HeaderFilterDefaultMapper)
						.toArray();
					this.actionByHeaderFilter = l
						.extractActionCreators(NoopValue)
						.map(HeaderFilterDefaultMapper)
						.toArray();

					this.connectorListSubject.next(l);
					if (this.grid) {
						this.grid.selectedRowKeys = [];
					}
				})
			)
			.subscribe();
	}

	public refresh(): void {
		this.firstLoadSubject.next(true);
		this.grid?.instance.beginCustomLoading('');
		this.store.dispatch(refreshConnectorList());
	}

	public search(searchTerm: string): void {
		if (this.grid === null) {
			return;
		}
		this.grid.instance.searchByText(searchTerm.trim());
	}

	public onSelectionChange($event: SelectionChangedEvent): void {
		this.toggleActions = $event.selectedRowsData.length > 0;
		this.selectionCount = $event.selectedRowsData.length;
	}

	public async onGridContentReady(): Promise<void> {
		const firstLoad = await firstValueFrom(this.firstLoadSubject);

		if (firstLoad) {
			await firstValueFrom(
				timer(1000).pipe(
					tap(() => {
						this.firstLoadSubject.next(false);
						this.grid?.instance.endCustomLoading();
					})
				)
			);
		}
	}

	public rowMenuOpen($event: MouseEvent, rowIndex: number): void {
		$event.stopPropagation();
		// fake show menu button
		setTimeout(() => {
			this.currentlyHovered = rowIndex;
		}, 300);
	}

	public onCellHover(event: CellHoverChangedEvent): void {
		if (event.eventType === 'mouseover') {
			this.currentlyHovered = event.rowIndex;
		} else {
			this.currentlyHovered = -1;
		}
	}

	public createNewConnector(): void {
		this.store.dispatch(navigateToConnectorCreate());
	}

	public async navigateToConnector(
		connector: ConnectorItemEntity
	): Promise<void> {
		if (!connector.isInSameProject && connector.isProjectPublic) {
			const previous = await firstValueFrom(this.openedOverlaySubject);
			this.openedOverlaySubject.next({
				detailsConnectorId:
					previous.detailsConnectorId === connector.id
						? undefined
						: connector.id,
			});
			return this.store.dispatch(
				fetchPublicProjectDetails({
					connectorId: connector.id,
				})
			);
		}
		this.store.dispatch(
			navigateToConnector({
				id: connector.id,
				connectorType: connector.type,
			})
		);
	}

	public duplicateConnector(
		connector: ConnectorItemEntity,
		event?: Event
	): void {
		if (event) {
			event.stopPropagation();
		}

		this.store.dispatch(
			navigateToConnectorDuplicate({
				id: connector.id,
				connectorType: connector.type,
			})
		);
	}

	public createDepotFromConnector(
		event: Event,
		connector: ConnectorItemEntity
	): void {
		event.stopPropagation();
		this.store.dispatch(
			navigateToDepotFromConnector({
				id: connector.id,
			})
		);
	}

	public deleteConnector(event: Event, connector: ConnectorItemEntity): void {
		event.stopPropagation();
		return this.store.dispatch(
			openDeleteConfirmationPopup({
				connectorId: [connector.id],
				connectorLabel: connector.label,
			})
		);
	}

	public toggleIncludePublic(value: MatSelectChange): void {
		this.store.dispatch(
			updateIncludePublic({
				includePublic: value.value,
			})
		);
	}

	public calculateTagsHeaderFilterExpression(
		value: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: ConnectorItemEntity): boolean => {
			const tagsSet = column.calculateCellValue(
				data
			) as OrderedSet<AppTagItemEntity>;
			const tags = tagsSet.toList().toArray();
			if (tags.length === 0 && value === NoopValue) {
				return true;
			}

			return (
				!!tags &&
				!!tags.find((v) => checkStringEquality(v.label, value, filterOperation))
			);
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	public calculateActionByHeaderFilterExpression(
		value: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: ConnectorItemEntity): boolean => {
			const entity = column.calculateCellValue(data) as AvatarEntity;
			if (entity === null && value === NoopValue) {
				return true;
			}
			if (!entity) {
				return false;
			}
			const { name } = entity;
			return !!name && checkStringEquality(name, value, filterOperation);
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	public async openPublicProjectDetailsPopover(
		event: Event,
		elemId: number
	): Promise<void> {
		event.stopPropagation();
		const previous = await firstValueFrom(this.openedOverlaySubject);
		if (previous.detailsConnectorId === elemId) {
			this.openedOverlaySubject.next({
				...previous,
				detailsConnectorId: undefined,
			});
			return;
		}
		this.openedOverlaySubject.next({
			...previous,
			detailsConnectorId: elemId,
		});
		this.store.dispatch(
			fetchPublicProjectDetails({
				connectorId: elemId,
			})
		);
	}

	public closeOverlay(): void {
		this.openedOverlaySubject.next({
			detailsConnectorId: undefined,
		});
	}

	private async deleteSelected(): Promise<void> {
		if (!this.grid) {
			return;
		}
		const selectedRowKeys = this.grid.selectedRowKeys as Array<string>;
		const selectedConnectors = await firstValueFrom(
			this.connectorSelector.getSelectedConnectors$(selectedRowKeys)
		);
		if (selectedConnectors.length === 1) {
			return this.store.dispatch(
				openDeleteConfirmationPopup({
					connectorId: [selectedConnectors[0].id],
					connectorLabel: selectedConnectors[0].label,
				})
			);
		}
		return this.store.dispatch(
			openDeleteConfirmationPopup({
				connectorId: selectedConnectors.map((k) => k.id),
				connectorLabel: '',
			})
		);
	}

	private toggleActivationStatus(connector: ConnectorItemEntity): void {
		this.store.dispatch(
			toggleConnectorStatus({
				connectorId: connector.id,
				connectorType: connector.type,
			})
		);
	}

	private async bootstrapIcons(): Promise<void> {
		const actions = [
			ActionIcons.ColumnExpositionChooser,
			ActionIcons.Delete,
			ActionIcons.Filter,
			ActionIcons.MoveDatablock,
		];
		const actionMap = await firstValueFrom(
			this.actionIcons.getFromRegistry(...actions)
		);
		this.panelActions = [
			{
				order: 0,
				command: (): void => {
					this.grid?.instance.showColumnChooser();
				},
				iconName: actionMap.get(ActionIcons.ColumnExpositionChooser)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@connector.list.columnSelection:`,
				typed: '',
			},
			{
				order: 2,
				command: (): Promise<void> => this.deleteSelected(),
				iconName: actionMap.get(ActionIcons.Delete)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@connector.list.deleteSelection:`,
				typed: 'plain-context-action',
			},
		];
	}

	private buildPublicProjectDetails(
		e: ConnectorItemEntity | undefined
	): AppPublicProjectView {
		if (!e) {
			return AppPublicProjectView.build();
		}
		return AppPublicProjectView.build({
			projectLabel: e.projectLabel,
			pictoUrl: e.projectPictoUrl,
			elemLabel: e.label,
			elemCode: e.code,
			elemId: e.id,
			createdOn: e.creationDate,
			createdBy: e.createdByAvatar,
			updatedBy: e.updatedByAvatar,
			updatedOn: e.updateDate,
		});
	}
}
